/**
 * ScenarioBulkActionBar.tsx
 * FEAT-2026-0007 Phase 6F.4 — Bulk action bar for multi-select mode.
 *
 * Phase 1 (machine planning):
 *   - Bulk Assign Machine: picks one machine, calls placeTaskOnMachine /
 *     changeBookingMachine per task. Tasks whose TaskTypeKey has no capable
 *     match for the chosen machine are skipped + reported.
 *   - Bulk Set Machine Qty: applies one MachineCount to all selected tasks
 *     via updateTask.
 *
 * Phase 2 (employee planning):
 *   - Bulk Assign Employee: picks one user from assignable-users, calls
 *     bulkAssignFromTask per task with scenarioKey (presence — percent 100).
 *     409 skill-gate / day-off-conflict per task are caught; task is skipped
 *     + reported.
 *
 * NOTE: The loop is intentionally sequential for v1 to keep error isolation
 * clean and avoid API burst (chatty but correct). A parallel batch endpoint
 * could replace this in a future optimisation pass.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { alpha, useTheme } from '@mui/material/styles';

import {
  listCapableMachines,
  placeTaskOnMachine,
  changeBookingMachine,
  updateTask,
  type TaskWithBookings,
  type CapableMachine,
} from '../../../../services/scheduleTasks';
import {
  listAssignableUsers,
  bulkAssignFromTask,
  weekOverloadHint,
  SkillGateError,
  AbsenceDayConflictError,
  type AssignableUser,
} from '../../../../services/scheduleAssignments';

// ---------------------------------------------------------------------------
// Shared result summary helper
// ---------------------------------------------------------------------------

interface BulkResult {
  applied: number;
  skipped: number;
  skippedNames: string[];
  message: string;
}

// ---------------------------------------------------------------------------
// Phase 1 Bulk Action Bar
// ---------------------------------------------------------------------------

export interface Phase1BulkActionBarProps {
  /** Task rows that are currently checked. */
  selectedTasks: TaskWithBookings[];
  scenarioKey: string | null;
  /** Called after batch completes so the parent can refresh + clear selection. */
  onDone: () => void;
}

/**
 * Renders the bulk-machine-action bar shown below the Phase-1 task table
 * whenever one or more rows are selected in Edit Multiple mode.
 */
export const Phase1BulkActionBar: React.FC<Phase1BulkActionBarProps> = ({
  selectedTasks,
  scenarioKey,
  onDone,
}) => {
  const theme = useTheme();

  // ── State ─────────────────────────────────────────────────────────────────
  const [capableMachinesByType, setCapableMachinesByType] = useState<
    Record<string, CapableMachine[]>
  >({});
  const [loadingMachines, setLoadingMachines] = useState(false);

  // All distinct capable machines across all selected tasks (intersection for
  // "safe" assignment; we compute per-task capability at apply time).
  const [allMachines, setAllMachines] = useState<CapableMachine[]>([]);
  const [pickedMachine, setPickedMachine] = useState<CapableMachine | null>(null);

  // Machine qty bulk-set
  const [qtyInput, setQtyInput] = useState('');

  const [applyingMachine, setApplyingMachine] = useState(false);
  const [applyingQty, setApplyingQty] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  // ── Load capable machines for the selected task types ────────────────────
  const loadCapableMachines = useCallback(async () => {
    if (selectedTasks.length === 0) return;
    setLoadingMachines(true);
    try {
      const distinctTypeKeys = [
        ...new Set(
          selectedTasks
            .map((t) => t.TaskTypeKey)
            .filter((k): k is string => k !== null),
        ),
      ];

      const entries = await Promise.all(
        distinctTypeKeys.map(async (key) => {
          const machines = await listCapableMachines(key);
          return [key, machines] as const;
        }),
      );

      const byType: Record<string, CapableMachine[]> = {};
      for (const [k, m] of entries) byType[k] = m;
      setCapableMachinesByType(byType);

      // Union all machines — the user picks one; we check per-task capability
      // at apply time.
      const seen = new Set<string>();
      const union: CapableMachine[] = [];
      for (const machines of Object.values(byType)) {
        for (const m of machines) {
          if (!seen.has(m.MachineKey)) {
            seen.add(m.MachineKey);
            union.push(m);
          }
        }
      }
      union.sort((a, b) => a.Code.localeCompare(b.Code));
      setAllMachines(union);
    } catch {
      setAllMachines([]);
    } finally {
      setLoadingMachines(false);
    }
  }, [selectedTasks]);

  useEffect(() => {
    void loadCapableMachines();
  }, [loadCapableMachines]);

  // ── Apply bulk machine assign ─────────────────────────────────────────────
  const handleBulkAssignMachine = async () => {
    if (!pickedMachine || selectedTasks.length === 0) return;
    setApplyingMachine(true);
    setResult(null);
    let applied = 0;
    let skipped = 0;
    const skippedNames: string[] = [];

    for (const task of selectedTasks) {
      // Check capability: is this machine in the task's capable list?
      const capableForTask =
        task.TaskTypeKey == null
          ? []
          : (capableMachinesByType[task.TaskTypeKey] ?? []);

      const isCapable = capableForTask.some(
        (m) => m.MachineKey === pickedMachine.MachineKey,
      );

      if (!isCapable) {
        skipped++;
        skippedNames.push(task.Name);
        continue;
      }

      try {
        if (task.Bookings.length === 0) {
          // No booking yet — place the first machine
          await placeTaskOnMachine(task.ScheduleTaskKey, pickedMachine.MachineKey);
        } else {
          // Change each existing booking to the new machine
          for (const booking of task.Bookings) {
            await changeBookingMachine(booking.MachineBookingKey, pickedMachine.MachineKey);
          }
        }
        applied++;
      } catch {
        skipped++;
        skippedNames.push(task.Name);
      }
    }

    setResult({
      applied,
      skipped,
      skippedNames,
      message:
        skipped > 0
          ? `Applied to ${applied} task${applied !== 1 ? 's' : ''}, skipped ${skipped} (incapable or error).`
          : `Applied ${pickedMachine.Code} to ${applied} task${applied !== 1 ? 's' : ''}.`,
    });
    setApplyingMachine(false);
    setPickedMachine(null);
    onDone();
  };

  // ── Apply bulk qty set ────────────────────────────────────────────────────
  const handleBulkSetQty = async () => {
    const qty = parseInt(qtyInput, 10);
    if (!Number.isFinite(qty) || qty < 1 || selectedTasks.length === 0) return;
    setApplyingQty(true);
    setResult(null);
    let applied = 0;
    let skipped = 0;
    const skippedNames: string[] = [];

    for (const task of selectedTasks) {
      try {
        await updateTask(task.ScheduleTaskKey, { MachineCount: qty });
        applied++;
      } catch {
        skipped++;
        skippedNames.push(task.Name);
      }
    }

    setResult({
      applied,
      skipped,
      skippedNames,
      message:
        skipped > 0
          ? `Set qty=${qty} on ${applied} task${applied !== 1 ? 's' : ''}, skipped ${skipped} (error).`
          : `Set Machine Qty=${qty} on ${applied} task${applied !== 1 ? 's' : ''}.`,
    });
    setApplyingQty(false);
    setQtyInput('');
    onDone();
  };

  const busy = applyingMachine || applyingQty;

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: `1px solid ${alpha(theme.schedule.machineTime, 0.4)}`,
        backgroundColor: alpha(theme.schedule.machineTime, 0.05),
        px: 2,
        py: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DoneAllIcon sx={{ fontSize: 16, color: theme.schedule.machineTime }} />
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: theme.schedule.machineTime, fontSize: '0.8rem' }}
        >
          Bulk Actions — {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''} selected
        </Typography>
      </Box>

      <Divider />

      {/* Action row */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-end',
        }}
      >
        {/* ── Bulk Assign Machine ─────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, minWidth: 280 }}>
          <PrecisionManufacturingIcon
            sx={{ fontSize: 18, color: theme.schedule.machineTime, mb: 0.5 }}
          />
          <Autocomplete<CapableMachine>
            size="small"
            sx={{ width: 240 }}
            options={allMachines}
            value={pickedMachine}
            onChange={(_e, v) => setPickedMachine(v)}
            loading={loadingMachines}
            getOptionLabel={(m) => `${m.Code}: ${m.Name}`}
            isOptionEqualToValue={(a, b) => a.MachineKey === b.MachineKey}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign machine to selected"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingMachines && <CircularProgress size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={busy}
          />
          <Button
            size="small"
            variant="contained"
            onClick={() => { void handleBulkAssignMachine(); }}
            disabled={!pickedMachine || busy}
            sx={{
              textTransform: 'none',
              fontSize: '0.78rem',
              backgroundColor: theme.schedule.machineTime,
              '&:hover': { backgroundColor: alpha(theme.schedule.machineTime, 0.85) },
            }}
          >
            {applyingMachine ? <CircularProgress size={14} color="inherit" /> : 'Apply'}
          </Button>
        </Box>

        {/* ── Bulk Set Machine Qty ────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <TextField
            size="small"
            label="Set qty for selected"
            type="number"
            value={qtyInput}
            onChange={(e) => setQtyInput(e.target.value)}
            disabled={busy}
            inputProps={{ min: 1, style: { width: 48 } }}
            sx={{ width: 140 }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => { void handleBulkSetQty(); }}
            disabled={
              !qtyInput ||
              !Number.isFinite(parseInt(qtyInput, 10)) ||
              parseInt(qtyInput, 10) < 1 ||
              busy
            }
            sx={{ textTransform: 'none', fontSize: '0.78rem' }}
          >
            {applyingQty ? <CircularProgress size={14} /> : 'Set Qty'}
          </Button>
        </Box>
      </Box>

      {/* Result banner */}
      {result && (
        <Alert
          severity={result.skipped > 0 ? 'warning' : 'success'}
          onClose={() => setResult(null)}
          sx={{ fontSize: '0.8rem', py: 0.5 }}
        >
          {result.message}
          {result.skippedNames.length > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Skipped: {result.skippedNames.join(', ')}
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Phase 2 Bulk Action Bar
// ---------------------------------------------------------------------------

export interface Phase2BulkActionBarProps {
  /** Task rows that are currently checked. */
  selectedTasks: TaskWithBookings[];
  scenarioKey: string | null;
  /** Called after batch completes so the parent can refresh + notify capacity. */
  onDone: () => void;
}

/**
 * Phase-2 bulk action bar: assign one employee across all selected tasks.
 * Uses bulkAssignFromTask (the same service the inline picker uses).
 * 409 skill-gate or day-off-conflict per task are caught; that task is
 * skipped with an explanation in the summary.
 */
export const Phase2BulkActionBar: React.FC<Phase2BulkActionBarProps> = ({
  selectedTasks,
  scenarioKey,
  onDone,
}) => {
  const theme = useTheme();

  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [pickedUser, setPickedUser] = useState<AssignableUser | null>(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  // Load assignable users once
  useEffect(() => {
    if (selectedTasks.length === 0) return;
    setLoadingUsers(true);
    listAssignableUsers()
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [selectedTasks.length]);

  const handleBulkAssignEmployee = async () => {
    if (!pickedUser || selectedTasks.length === 0) return;
    setApplying(true);
    setResult(null);
    let applied = 0;
    let skipped = 0;
    const skippedNames: string[] = [];

    let overloadHint: string | null = null;
    for (const task of selectedTasks) {
      try {
        const res = await bulkAssignFromTask({
          scheduleTaskKey: task.ScheduleTaskKey,
          userPercents: [{ userKey: pickedUser.AppUserKey, percent: 100 }],
          scenarioKey: scenarioKey ?? undefined,
        });
        // G2-7: keep the last non-blocking overload advisory for the summary.
        overloadHint = weekOverloadHint(res?.weekOverloaded) ?? overloadHint;
        applied++;
      } catch (err) {
        skipped++;
        const reason =
          err instanceof SkillGateError
            ? 'skill gate'
            : err instanceof AbsenceDayConflictError
              ? 'day off conflict'
              : 'error';
        skippedNames.push(`${task.Name} (${reason})`);
      }
    }

    setResult({
      applied,
      skipped,
      skippedNames,
      message:
        (skipped > 0
          ? `Assigned ${pickedUser.DisplayName} to ${applied} task${applied !== 1 ? 's' : ''}, skipped ${skipped}.`
          : `Assigned ${pickedUser.DisplayName} to ${applied} task${applied !== 1 ? 's' : ''}.`)
          // G2-7: the non-blocking weekly-overload advisory rides the summary.
          + (overloadHint ? ` ${overloadHint}` : ''),
    });
    setApplying(false);
    setPickedUser(null);
    onDone();
  };

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: `1px solid ${alpha(theme.schedule.employeeAlloc, 0.4)}`,
        backgroundColor: alpha(theme.schedule.employeeAlloc, 0.05),
        px: 2,
        py: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DoneAllIcon sx={{ fontSize: 16, color: theme.schedule.employeeAlloc }} />
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: theme.schedule.employeeAlloc, fontSize: '0.8rem' }}
        >
          Bulk Actions — {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''} selected
        </Typography>
      </Box>

      <Divider />

      {/* Action row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
        <PersonAddIcon sx={{ fontSize: 18, color: theme.schedule.employeeAlloc, mb: 0.5 }} />
        <Autocomplete<AssignableUser>
          size="small"
          sx={{ width: 260 }}
          options={users}
          value={pickedUser}
          onChange={(_e, v) => setPickedUser(v)}
          loading={loadingUsers}
          getOptionLabel={(u) => `${u.DisplayName}${u.Tier ? ` (${u.Tier})` : ''}`}
          isOptionEqualToValue={(a, b) => a.AppUserKey === b.AppUserKey}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assign employee to selected"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers && <CircularProgress size={14} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={applying}
        />
        <Button
          size="small"
          variant="contained"
          onClick={() => { void handleBulkAssignEmployee(); }}
          disabled={!pickedUser || applying}
          sx={{
            textTransform: 'none',
            fontSize: '0.78rem',
            backgroundColor: theme.schedule.employeeAlloc,
            '&:hover': { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.85) },
          }}
        >
          {applying ? <CircularProgress size={14} color="inherit" /> : 'Assign All'}
        </Button>
      </Box>

      {/* Result banner */}
      {result && (
        <Alert
          severity={result.skipped > 0 ? 'warning' : 'success'}
          onClose={() => setResult(null)}
          sx={{ fontSize: '0.8rem', py: 0.5 }}
        >
          {result.message}
          {result.skippedNames.length > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Skipped: {result.skippedNames.join(', ')}
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
};
